// Shared behavior for any slider-component that uses a leading/trailing decoy
// clone so there's always a peek at both track edges (see the .peek-slide--decoy
// CSS comment in sections/projects.liquid and sections/sub-categories.liquid -
// this file only handles the JS side, layout/sizing stays with each section's
// own BEM classes). Opt in by adding, on top of the markup SliderComponent
// (global.js) already requires - a track div with id="Slider-{{ section.id }}"
// and, if the section wants prev/next controls, a `.slider-buttons` element
// with the standard slider-button--prev/--next buttons inside it:
// - `data-peek-carousel` on the <slider-component> element itself
// - `peek-slide` / `peek-slide--decoy` classes (alongside the section's own
//   BEM slide class) on its slides
// Dots (.slider-counter__link--dots) are optional - wired up automatically
// when present, skipped otherwise (see sections/sub-categories.liquid, which
// has none).
//
// Guarded so including this file from more than one section on the same page
// (e.g. a future page using both projects and sub-categories) only runs the
// init once - every matching slider is already in the DOM by the time this
// deferred script runs, so a second run would just re-observe the same
// elements and double-fire the peek/resize/scroll handlers. Same one-shot
// timing as the two per-section scripts this replaced (each ran its own
// single querySelectorAll pass too) - a slider added to the DOM after this
// script runs (e.g. a live Theme Editor section-add) still won't be picked
// up, guard or not.
if (!window.peekCarouselInitialized) {
  window.peekCarouselInitialized = true;

  document.querySelectorAll('[data-peek-carousel]').forEach((slider) => {
    // Each slider used to be set up by its own independent top-level script
    // execution, so a failure in one (e.g. IntersectionObserver/ResizeObserver
    // unsupported) never touched any other. Sharing one forEach loses that
    // isolation - NodeList.forEach aborts the remaining iterations on a throw
    // - so catch per-slider instead of letting one bad slider take the rest
    // down with it.
    try {
      setUpPeekCarousel(slider);
    } catch (error) {
      console.error('peek-carousel.js: failed to set up slider', slider, error);
    }
  });

  function setUpPeekCarousel(slider) {
    const dots = slider.querySelectorAll('.slider-counter__link--dots');
    const buttons = slider.querySelector('.slider-buttons');
    const track = slider.querySelector('[id^="Slider-"]');
    const realSlides = slider.querySelectorAll('.peek-slide:not(.peek-slide--decoy)');
    const leadingDecoy = slider.querySelector('.peek-slide--decoy');

    if (dots.length) {
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          if (!realSlides[index]) return;
          realSlides[index].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        });
      });
    }

    if (!track) return;

    // The leading decoy (a clone of the last real slide, see the CSS comment
    // on .peek-slide--decoy) sits before the first real slide so there's
    // always something to peek at on the left, even on first load - but the
    // browser's natural scrollLeft: 0 rest position would show the decoy
    // itself as the "active" slide instead of just a peeking sliver of it.
    // Skip past it so the real first slide starts exactly where it always
    // used to (matches realSlides[0]'s own start-align math, not a fixed
    // pixel guess). Re-checked on resize too: crossing a slider's stacked-
    // column breakpoint (if it has one) and back resets scrollLeft to 0.
    const realStart = () => (leadingDecoy && realSlides.length ? realSlides[0].offsetLeft - leadingDecoy.offsetLeft : 0);

    // Shared by the loop-jump click handler and updateActiveDot below - both
    // need "have we scrolled as far right as the track physically allows"
    // (the trailing decoy's own peek still has room beyond the last real
    // slide, so this is the track's true max, not just "last real slide
    // visible"). Kept as one function so a future tolerance/RTL fix only
    // has to happen in one place.
    const isAtTrackEnd = () => track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;

    const clampPastLeadingDecoy = () => {
      if (!leadingDecoy || !realSlides.length) return;
      if (track.scrollLeft < realStart()) {
        track.scrollLeft = realStart();
      }
    };

    clampPastLeadingDecoy();

    // SliderComponent (global.js) recalculates slider.totalPages on resize but
    // only dispatches slideChanged when the current page itself changes, so a
    // resize that flips between "fits, nothing to scroll" and "needs paging"
    // without moving the current page needs its own observer to catch it.
    // totalPages itself is skewed by the decoys inflating the offsetLeft
    // global.js measures slidesPerPage from, so pagination visibility is
    // decided from actual scrollable overflow instead of trusting it.
    const updatePaginationVisibility = () => {
      if (!buttons) return;
      buttons.style.display = track.scrollWidth > track.clientWidth + 1 ? '' : 'none';
    };

    let updateActiveDot = () => {};

    // Opt-in (data-loop-slider, e.g. sections/projects.liquid): the leading/
    // trailing decoys already visually preview the wrap-around neighbor, so
    // prev/next should complete that loop instead of disabling at the real
    // ends - global.js's SliderComponent already has an unused escape hatch
    // for exactly this (enableSliderLooping skips its own disable-at-bounds
    // logic in update()), it's just never set outside SlideshowComponent.
    // Handled here instead of by generalizing SlideshowComponent's own
    // wrap-around (onButtonClick calls super.onButtonClick unconditionally,
    // so any wrap logic added to the base class's onButtonClick would also
    // run for SlideshowComponent, on top of its own existing isFirstSlide/
    // isLastSlide handling - decoy-aware wrapping doesn't belong in a base
    // class other slider variants share anyway).
    if (slider.hasAttribute('data-loop-slider') && buttons && realSlides.length) {
      slider.enableSliderLooping = true;

      // SliderComponent's own constructor (global.js) already found these by
      // button[name="next"/"previous"] and stashed them on the element -
      // reuse that instead of a second, independent .slider-button--next/
      // --prev class-based lookup that could silently drift out of sync
      // with global.js's own contract.
      const nextButton = slider.nextButton;
      const prevButton = slider.prevButton;

      // global.js is an earlier <script defer> (theme.liquid's <head>), so
      // its customElements.define call - and the synchronous constructor-time
      // update() that runs for every <slider-component> already in the DOM -
      // fires before this deferred script (loaded from inside the section
      // itself) ever gets a chance to set enableSliderLooping above. If that
      // first update() measures the slides before this section's own
      // component-slider.css has finished loading (a real risk on a real
      // network - the stylesheet is requested from inside the section, not
      // preloaded in <head>), unstyled/pre-flex layout can make the last
      // slide read as already fully visible and permanently disable
      // nextButton - update() always bails out early once enableSliderLooping
      // is true, so nothing downstream ever clears it again. Clear it here,
      // once, right after opting in.
      if (nextButton) nextButton.removeAttribute('disabled');
      if (prevButton) prevButton.removeAttribute('disabled');

      const slidePosition = (index) => (leadingDecoy ? realSlides[index].offsetLeft - leadingDecoy.offsetLeft : realSlides[index].offsetLeft);

      // Delegated to `buttons` (the .slider-buttons wrapper, an ancestor of
      // the actual <button> elements) in the capture phase, not attached to
      // the buttons themselves. global.js's own onButtonClick listener (added
      // in the SliderComponent constructor, bubble phase) runs a scrollTo of
      // its own on the same click - reading track.scrollLeft after it would
      // see that scroll's already-applied result, not the position the click
      // actually started from. Capture vs. bubble only reorders listeners
      // across the ancestor traversal; at the event's own target (a real
      // click lands on the 44x44 button itself, not its tiny icon) capture
      // vs. bubble doesn't apply and registration order wins instead - since
      // global.js's listener is attached first (it loads earlier), a
      // capture-phase listener on the button itself would still lose that
      // race for most clicks. Listening on the wrapper instead means this
      // fires during genuine capture (button hasn't been reached yet),
      // which is unconditionally first regardless of registration order.
      if (nextButton || prevButton) {
        buttons.addEventListener(
          'click',
          (event) => {
            if (nextButton && event.target.closest('button') === nextButton && isAtTrackEnd()) {
              event.stopImmediatePropagation();
              slider.setSlidePosition(slidePosition(0));
            } else if (prevButton && event.target.closest('button') === prevButton && track.scrollLeft <= realStart() + 2) {
              event.stopImmediatePropagation();
              slider.setSlidePosition(slidePosition(realSlides.length - 1));
            }
          },
          true
        );
      }
    }

    updatePaginationVisibility();
    new ResizeObserver(() => {
      clampPastLeadingDecoy();
      updatePaginationVisibility();
      // global.js's own ResizeObserver on this same element can flip
      // nextButton's disabled state (see updateActiveDot below) without ever
      // firing a `scroll` event, e.g. a pure width change that changes how
      // many real slides fit per page - re-check the active dot here too,
      // not just on scroll.
      updateActiveDot();
    }).observe(track);

    if (dots.length) {
      // SliderComponent's own currentPage (global.js) is "which real slide is
      // flush against the left edge", which is exactly right for ordinary
      // next/prev progression - reaching real slide N via next legitimately
      // makes dot N active even if slides after it are already visible too.
      // It only breaks down once scrolling has gone further than any next
      // click ever can: clicking dot N+1/N+2 directly (via scrollIntoView)
      // can clamp short of their own flush position once there's no more
      // scrollable room, landing at the same scrollLeft currentPage would
      // derive for an earlier, already-reachable dot - nextButton disables
      // at that same "nothing more to reveal" point, but earlier than the
      // true end of the scrollable track (the trailing decoy's peek still
      // has room beyond it), so nextButton.disabled alone can't tell "we
      // organically arrived here via next" apart from "a dot click forced us
      // past where next stops" - only being at the track's actual maximum
      // scrollLeft can (isAtTrackEnd, defined above).
      let lastActiveIndex = -1;
      updateActiveDot = () => {
        if (!Number.isFinite(slider.currentPage)) return;

        const pageOffset = leadingDecoy ? 1 : 0;
        let activeIndex = slider.currentPage - 1 - pageOffset;
        if (track.scrollLeft <= realStart() + 2) {
          activeIndex = 0;
        } else if (isAtTrackEnd()) {
          activeIndex = realSlides.length - 1;
        }

        if (activeIndex === lastActiveIndex) return;
        lastActiveIndex = activeIndex;

        dots.forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.classList.toggle('slider-counter__link--active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      };

      // Reads slider.currentPage directly instead of listening for
      // slideChanged, and checks nextButton's freshly-updated disabled
      // attribute - both are only current once global.js's own scroll
      // listener (bound in the SliderComponent constructor, before this
      // deferred script ever runs) has finished, so this is a second `scroll`
      // listener on the same element rather than reacting to slideChanged,
      // which fires mid-way through global.js's update() before it sets
      // nextButton's disabled state for the new position.
      track.addEventListener('scroll', updateActiveDot);
    }

    // Real slides only partially inside the track's visible area get the
    // smaller/dimmer "peeking" treatment instead of looking like a cropped
    // full-strength card. Decoys carry that treatment permanently in the
    // markup instead of through this ratio check - at the very end of the
    // track there isn't enough remaining scroll room to leave the trailing
    // decoy only partially visible, so a ratio-driven toggle would let it
    // flash to full strength there instead of always reading as a receded
    // peek. On a stacked-column mobile layout (if the section has one) the
    // track has no clipping viewport of its own, so every real slide
    // reports fully intersecting and never gets the class.
    if (realSlides.length) {
      const peekObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle('is-peeking', entry.intersectionRatio < 0.98);
          });
        },
        { root: track, threshold: [0, 0.98, 1] }
      );
      realSlides.forEach((slide) => peekObserver.observe(slide));
    }
  }
}
